import { createEntityAdapter, createSlice, type SliceCaseReducers } from '@reduxjs/toolkit';
import { AppThunk } from '../store';

export interface MessagePayload {
  messageCode: string;
  substitutions?: Record<string, string>;
}

interface MessagesState {
  errorMessages: string[] | MessagePayload[];
  successMessages: string[] | MessagePayload[];
  warningMessages: string[] | MessagePayload[];
}

const messagesAdapter = createEntityAdapter<MessagesState>();

export const messagesSlice = createSlice<MessagesState, SliceCaseReducers<MessagesState>, 'messages'>({
  name: 'messages',
  initialState: messagesAdapter.getInitialState({
    errorMessages: [],
    successMessages: [],
    warningMessages: [],
  }),
  reducers: {
    sendErrorMessageAction(state, action) {
      state.errorMessages.push(action.payload);
    },
    sendSuccessMessageAction(state, action) {
      state.successMessages.push(action.payload);
    },
    sendWarningMessageAction(state, action) {
      state.warningMessages.push(action.payload);
    },
    clearErrorMessagesAction(state) {
      state.errorMessages = [];
    },
    clearSuccessMessagesAction(state) {
      state.successMessages = [];
    },
    clearWarningMessagesAction(state) {
      state.warningMessages = [];
    },
    clearMessagesAction(state) {
      state.errorMessages = [];
      state.successMessages = [];
      state.warningMessages = [];
    },
  },
});

export const { reducer: messagesReducer } = messagesSlice;

const {
  sendErrorMessageAction,
  sendSuccessMessageAction,
  sendWarningMessageAction,
  clearErrorMessagesAction,
  clearSuccessMessagesAction,
  clearWarningMessagesAction,
  clearMessagesAction,
} = messagesSlice.actions;

export const sendErrorMessage = (message: string): AppThunk => {
  return async (dispatch) => {
    dispatch(sendErrorMessageAction(message));
  };
};
export const sendSuccessMessage = (message: string): AppThunk => {
  return async (dispatch) => {
    dispatch(sendSuccessMessageAction(message));
  };
};
export const sendWarningMessage = (message: string): AppThunk => {
  return async (dispatch) => {
    dispatch(sendWarningMessageAction(message));
  };
};
// Allow non-React components to dispatch messages via constants
export const sendErrorMessageWithCode = (messageCode: string, substitutions?: Record<string, string>): AppThunk => {
  return async (dispatch) => {
    dispatch(sendErrorMessageAction({ messageCode, substitutions }));
  };
};
export const sendSuccessMessageWithCode = (messageCode: string, substitutions?: Record<string, string>): AppThunk => {
  return async (dispatch) => {
    dispatch(sendSuccessMessageAction({ messageCode, substitutions }));
  };
};
export const sendWarningMessageWithCode = (messageCode: string, substitutions?: Record<string, string>): AppThunk => {
  return async (dispatch) => {
    dispatch(sendWarningMessageAction({ messageCode, substitutions }));
  };
};
export const clearErrorMessages = (): AppThunk => {
  return async (dispatch) => {
    dispatch(clearErrorMessagesAction(null));
  };
};
export const clearSuccessMessages = (): AppThunk => {
  return async (dispatch) => {
    dispatch(clearSuccessMessagesAction(null));
  };
};
export const clearWarningMessages = (): AppThunk => {
  return async (dispatch) => {
    dispatch(clearWarningMessagesAction(null));
  };
};
export const clearAllMessages = (): AppThunk => {
  return async (dispatch) => {
    dispatch(clearMessagesAction(null));
  };
};
